import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { DropdownSelect } from '@sorare/core/src/atoms/inputs/DropdownSelect';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import {
  PlayerSearchViewAttributes,
  type TeamModeOverride as TeamModeOverrideValue,
} from '@sorare/core/src/lib/playerSearchView';

import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';
import { useTeamModeSupport } from '#src/searchCommonPlayer/TeamModeSupport.tsx';
import {
  type TeamMode,
  useTeamMode,
} from '#src/searchCommonPlayer/teamMode.ts';

const manualLabels = defineMessages<Exclude<TeamModeOverrideValue, 'AUTO'>>({
  ALL: { defaultMessage: 'All' },
  CLUB: { defaultMessage: 'Club' },
  NATIONAL_TEAM: { defaultMessage: 'National team' },
});

const autoLabels = defineMessages<TeamMode>({
  ALL: { defaultMessage: 'Auto (All)' },
  CLUB: { defaultMessage: 'Auto (Club)' },
  NATIONAL_TEAM: { defaultMessage: 'Auto (National team)' },
});

export const TeamModeOverride = () => {
  const { formatMessage } = useIntl();
  const { statsManager, updatePlayerSearchViewAttribute } =
    useManagePlayerSearchView();
  const override =
    statsManager[PlayerSearchViewAttributes.TEAM_MODE_OVERRIDE] ?? 'AUTO';

  const teamModeSupport = useTeamModeSupport();
  const urlTeamMode = useTeamMode();
  if (!teamModeSupport) return null;
  const { derivedTeamMode, withAutoMode } = teamModeSupport;

  // without AUTO, an unset override displays the effective (URL-driven) mode
  const value: TeamModeOverrideValue =
    !withAutoMode && override === 'AUTO' ? urlTeamMode : override;

  const options: {
    value: TeamModeOverrideValue;
    label: string;
  }[] = [
    ...(withAutoMode
      ? [
          {
            value: 'AUTO' as const,
            label: formatMessage(autoLabels[derivedTeamMode]),
          },
        ]
      : []),
    { value: 'ALL', label: formatMessage(manualLabels.ALL) },
    { value: 'CLUB', label: formatMessage(manualLabels.CLUB) },
    {
      value: 'NATIONAL_TEAM',
      label: formatMessage(manualLabels.NATIONAL_TEAM),
    },
  ];

  return (
    <Vertical gap={1}>
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage defaultMessage="Team scope" />
      </LabelM>
      <DropdownSelect<TeamModeOverrideValue>
        size="small"
        value={value}
        options={options}
        onChange={next =>
          updatePlayerSearchViewAttribute(
            PlayerSearchViewAttributes.TEAM_MODE_OVERRIDE,
            next
          )
        }
      />
    </Vertical>
  );
};
